<script setup>

import {computed, inject, ref} from "vue";
import { useI18n } from "vue-i18n";
import { DateTime, Settings } from 'luxon'
import { useCommunitiesStore } from "@/stores/communitiesStore";
import {formatDate} from "../lib/es-bill-shared-js/misc/timeTools";
import {useDsoInformationStore} from "@/stores/dsoCompanyInformationStore";
import {useCommunityStore} from "@/stores/communityStore";
const $log = inject('log');
const { t } = useI18n();
Settings.defaultZone = 'Europe/Vienna'; // might be redundant

const communitiesStore = useCommunitiesStore();

const communityStore = useCommunityStore()

const dsoInformationStore = useDsoInformationStore();
// async loading in background
dsoInformationStore.fetchDsoInformation();

const communityAvailable = computed(() => {
  const communities= communitiesStore.allCommunities;

  return communities.map(c => {
    return {
      title: c.name,
      props: { subtitle: c.ecid },
      ecn: c.ecn,
      ecid: c.ecid,
      community: c,
      overwriteEcnDso: (c.communityType === '16a' || c.communityType === '16c') ? c.ecnDso:undefined,
    };
  })
})

const dsoAvailable = computed(() => {
  return dsoInformationStore.informations.map(dsoInfo => {

    // need more?

    return {
      ...dsoInfo,
      title: dsoInfo.name,
      props: { subtitle: dsoInfo.ecn },
      ecn: dsoInfo.ecn,
    }
  });
});

const selection = ref({
  communityEntry: null,
  dsoEntry: null,
  defaultTimeRange: true,
  dateRange: [],
});

const dateStartDefault = DateTime.local(2020, 1, 1).toJSDate();
const dateEndDefault = DateTime.local().minus({days: 1}).
  set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate();

const requestParameters = computed(() => {
  let dr = [];
  if(selection.value.defaultTimeRange) {
    dr = [dateStartDefault, dateEndDefault]
  } else if(selection.value.dateRange.length === 1) {
    const dateStart = DateTime.fromISO(selection.value.dateRange[0]);
    $log.warn(dateStart)
    dr = [dateStart.toJSDate(), dateStart.toJSDate()];
  }
  else if(selection.value.dateRange.length > 1) {
    const dateStart = DateTime.fromISO(selection.value.dateRange[0]);
    let dateEnd = DateTime.fromISO(selection.value.dateRange[
      selection.value.dateRange.length-1]);

    dateEnd=dateEnd.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    dr = [dateStart.toJSDate(), dateEnd.toJSDate()]
  }

  let dsoToUse = selection.value.dsoEntry || null;
  if(selection.value.communityEntry?.overwriteEcnDso)
    dsoToUse = dsoInformationStore.informations.find(
      dso => dso.ecn === selection.value.communityEntry.overwriteEcnDso)

  return {
    community: selection.value.communityEntry?.community || null,
    dso: dsoToUse,
    dateRange: dr,
    canBeRequested: dr.length && selection.value.communityEntry && dsoToUse,
  }
})

const requestRunning = ref(false);
const requestFailed = ref(false);
async function startRequest() {
  if(!requestParameters.value.canBeRequested)
    return;

  requestRunning.value = true;
  requestFailed.value = false;

  try {
    await communityStore.startEcPodlist(
      requestParameters.value.community.ecn,
      requestParameters.value.community.ecid,
      requestParameters.value.dateRange[0],
      requestParameters.value.dateRange[1],
      requestParameters.value.dso.ecn,
    )
  } catch (e) {
    $log.error(e)
    requestFailed.value = true;
  }

  requestRunning.value = false;
}

</script>
<script>
export default {
  name: 'ViewCommunityRequestPodlist',
}
</script>


<template>
  <v-container class="containerBase pa-0">

    <v-container class="containerStandard">

      <p class="textHeadline">{{ $t('requestPodlist') }}</p>
      ANFODERUNG_ECP für 16a, 16b, 16c<br/>
      DSO für 16a, 16c aus community(Technical).ecnDso, Dropdown für 16b<br/>
      <br/>

      <v-combobox
        max-width="400px"
        bg-color="white"
        :class="{'mr-0': $vuetify.display.xs}"
        :placeholder="t('community')"
        v-model="selection.communityEntry"
        :items="communityAvailable"
        :filter-keys="['title', 'value']"
        density="comfortable"
        variant="outlined"
        item-value="ecid"
        rounded="2"
        hide-details
        clearable
      ></v-combobox>

      <v-combobox
        :disabled="selection.communityEntry?.overwriteEcnDso?.length"
        max-width="400px"
        bg-color="white"
        :class="{'mr-0': $vuetify.display.xs}"
        :placeholder="t('dso')"
        v-model="selection.dsoEntry"
        :items="dsoAvailable"
        :filter-keys="['title', 'value']"
        density="comfortable"
        variant="outlined"
        item-value="ecn"
        item-title="name"
        rounded="2"
        hide-details
        clearable
      ></v-combobox>

      <v-checkbox density="compact"
                  v-model="selection.defaultTimeRange"
                  :label="t('defaultTimeRange')"
                  hint="use 1.1.2020 to yesterday"
                  persistent-hint
                  max-width="200px"
                  class="mb-4 align-checkbox-start"
      />

      <v-date-picker v-if="!selection.defaultTimeRange"
                     v-model="selection.dateRange"
                     show-adjacent-months
                     hide-header
                     width="350" multiple='range'>
      </v-date-picker>

      Gemeinschaft: {{ requestParameters.community?.name}}<br/>
      ECID: {{ requestParameters.community?.ecid}}<br/>
      VNB: {{ requestParameters.dso?.name}}<br/>
      ECN-DSO: {{ requestParameters.dso?.ecn}}<br/>
      DateRange: {{ formatDate(requestParameters.dateRange[0]) }} bis inkl.
      {{ formatDate(requestParameters.dateRange[1]) }}<br/>

      <br/>
      <PrimaryButton
        :disabled="!requestParameters.canBeRequested"
        @click="startRequest"
        :text="$t('startRequest')"
      />

      <v-row v-if="requestRunning">
        <v-progress-circular class="mx-auto" color="blue-lighten-3" indeterminate></v-progress-circular>
      </v-row>
      <div v-if="requestFailed">
        Fehler bei Anfrage
      </div>



    </v-container>
  </v-container>
</template>

<style>


</style>
